// ═══════════════════════════════════════════════════════════════════
//  modules/wiki/Wiki.js
//  Wiki
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { THEME } from "../../socle/theme";
import { wikiApi } from "./api";
import { WikiAccueil } from "./WikiAccueil";
import { WikiCorbeille } from "./WikiCorbeille";
import { WikiEditeur } from "./WikiEditeur";
import { WikiHistorique } from "./WikiHistorique";
import { WikiListe } from "./WikiListe";
import { WikiPage } from "./WikiPage";
import { WikiStyles } from "./WikiStyles";

// ═══════════════ Composant principal ═══════════════
export function Wiki({ token, user, onNavigate, cible }) {
  const api = useRef(wikiApi(token)).current;
  const [vue, setVue] = useState("accueil");
  const [categorie, setCategorie] = useState(null);
  const [slug, setSlug] = useState(null);
  const [pageEditee, setPageEditee] = useState(null);
  const [pageHisto, setPageHisto] = useState(null);
  const [droits, setDroits] = useState({ creer: false, redacteur: false, fondateur: false });
  const [message, setMessage] = useState(null);
  const [preremplissage, setPreremplissage] = useState(null);

  useEffect(() => {
    api.arbre().then((a) => setDroits(a.droits)).catch(() => {});
  }, [api]);

  // Cible entrante (lot 3) : ouvrir une page précise, ou l'éditeur pré-rempli
  useEffect(() => {
    if (!cible) return;
    if (cible.type === "page" && cible.slug) {
      setPreremplissage(null); setSlug(cible.slug); setVue("page");
    } else if (cible.type === "creer") {
      setPreremplissage(cible); setPageEditee(null);
      setCategorie(cible.categorie || null); setVue("editeur");
    }
  }, [cible]);

  const ouvrir = (s) => { setSlug(s); setVue("page"); };
  const retourAccueil = () => { setVue("accueil"); setSlug(null); };

  // Clic sur une pastille : les pages s'ouvrent ici, le reste renvoie
  // vers le module concerné (l'ouverture directe d'un élément précis
  // arrivera au lot 3, quand les modules accepteront une cible).
  const surChip = (type, id, label, pageOrigine) => {
    if (type === "page") { ouvrir(id); return; }
    const modules = { ticket: "tickets", joueur: "erp", tache: "kanban", doc: "docs" };
    const module = modules[type];
    if (!module || !onNavigate) return;

    // Ticket et joueur s'ouvrent directement, avec le chemin du retour.
    if (type === "ticket" || type === "joueur") {
      onNavigate(module, {
        type, id, label,
        retour: pageOrigine ? { type: "page", slug: pageOrigine.slug, titre: pageOrigine.titre } : null,
      });
      return;
    }
    // Tâches et documents : le module s'ouvre, sans aller jusqu'à l'élément
    // (le Kanban passe par un projet puis une catégorie — trop de gymnastique
    // pour un cas rare, décidé avec l'équipe).
    setMessage(`Ouverture du module — cherche « ${label} ».`);
    setTimeout(() => onNavigate(module, null), 350);
  };

  return (
    <div>
      <WikiStyles />
      {message && (
        <div style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 8, padding: "8px 12px", fontSize: 12.5, marginBottom: 12 }}>
          {message}
        </div>
      )}

      {vue === "accueil" && (
        <WikiAccueil api={api} droits={droits}
          onOuvrir={ouvrir}
          onCategorie={(c) => { setCategorie(c); setVue("liste"); }}
          onCorbeille={() => setVue("corbeille")}
          onNouvelle={() => { setPageEditee(null); setCategorie(null); setVue("editeur"); }} />
      )}

      {vue === "liste" && (
        <WikiListe api={api} categorie={categorie} droits={droits}
          onRetour={retourAccueil}
          onOuvrir={ouvrir}
          onNouvelle={(c) => { setPageEditee(null); setCategorie(c); setVue("editeur"); }} />
      )}

      {vue === "page" && slug && (
        <WikiPage api={api} slug={slug} token={token}
          onRetour={() => (categorie ? setVue("liste") : retourAccueil())}
          onOuvrir={ouvrir}
          onModifier={(p) => { setPageEditee(p); setVue("editeur"); }}
          onHistorique={(p) => { setPageHisto(p); setVue("histo"); }}
          onChip={surChip} />
      )}

      {vue === "editeur" && (
        <WikiEditeur api={api} page={pageEditee} categorieParDefaut={categorie} preremplissage={preremplissage}
          droits={droits} user={user}
          onAnnuler={() => { setPreremplissage(null); setVue(pageEditee ? "page" : (categorie ? "liste" : "accueil")); }}
          onEnregistre={(s) => { setPreremplissage(null); setPageEditee(null); ouvrir(s); }} />
      )}

      {vue === "corbeille" && (
        <WikiCorbeille api={api} onRetour={retourAccueil} onOuvrir={ouvrir} />
      )}

      {vue === "histo" && pageHisto && (
        <WikiHistorique api={api} page={pageHisto} droits={droits}
          onRetour={() => setVue("page")}
          onRestaure={() => { setVue("page"); }} />
      )}
    </div>
  );
}
